import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import useAgeOptions from 'hooks/Search/useAgeOptions'
import { useCallback, useMemo } from 'react'
import type { ChangeEvent } from 'react'
import { useIntl } from 'react-intl'
import { AgeSelect } from './OccupancyChildAgeSelect'

interface Props {
  adultId: number
  age?: number
  minAge: number
  name: string
  onChange: (adultId: number, age: number) => void
}

function OccupancyAdultAgeSelect({ adultId, age, minAge, name, onChange }: Props) {
  const intl = useIntl()
  const ageOptions = useAgeOptions(150)
  const options = useMemo(() => ageOptions.slice(minAge), [ageOptions, minAge])
  const label = intl.formatMessage(
    {
      id: 'Oyrjq71VrYNxKGz98jfl',
      defaultMessage: 'Adult {adultNumber} age at tour start',
      description: 'Label for the selected age at tour start of an adult traveller on a tour whose adult age range includes children',
    },
    { adultNumber: adultId + 1 },
  )
  const requiredErrorMessage = intl.formatMessage({
    id: 'haQhZ0ImE-MPwb3P1wPi',
    defaultMessage: 'Please select the adult traveller’s age',
    description: 'Validation error when a required tour adult age has not been selected in the occupancy picker',
  })
  const selectPlaceholder = intl.formatMessage({
    id: 'wGdrR8Og2EyHnwyz7BOm',
    defaultMessage: 'Select',
    description: 'Placeholder text for child age dropdown in occupancy picker',
  })

  const onSelectChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    onChange(adultId, Number(event.currentTarget.value))
  }, [adultId, onChange])

  return <Group
    direction="horizontal"
    tabletDirection="vertical"
    horizontalAlign="space-between"
    gap={12}
    verticalAlign="space-between"
    style={{ position: 'relative' }}
  >
    <BodyText variant="medium">{label}</BodyText>
    <AgeSelect
      data-testid={`occupancy-adult-age-${adultId}`}
      aria-label={label}
      name={`${name}[${adultId}]::number`}
      value={age !== undefined && age >= minAge ? age : ''}
      onChange={onSelectChange}
      noValidationMessage
      noValidationSpacing
      requiredErrorMessage={requiredErrorMessage}
      placeholder={selectPlaceholder}
      required
    >
      {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
    </AgeSelect>
  </Group>
}

export default OccupancyAdultAgeSelect
